import { ChangeEvent, useRef } from 'react';
import * as React from 'react';
import { ButtonWithIcon, useModal } from '@blueprint/ui';
import { CloseSmall } from '@blueprint/icons';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import { getOptimizelyFeatureFlag } from '../optimizely';
import { OptimizelyDataFile } from '../optimizely.types';
import { OptimizelyOverrides } from '../optimizelyOverrides';

import { OptimizelyLogo } from './OptimizelyLogo';
import {
    StyledTable,
    Container,
    FixedButtonWithIcon,
    Overlay
} from './DevToggle.styles';

type Entry = Record<string, unknown> & {
    key: string;
    variations?: Record<string, unknown>[];
};
type StateResolver = (T: Entry) => unknown;
type onForceHandler = (urlParam: [string, string]) => void;

const getHeadings = (arrayOfObjects: Record<string, unknown>[]) => {
    return arrayOfObjects.reduce(
        (acc, obj) => {
            const k = Object.keys(obj);
            k.forEach((v) => {
                if (acc.indexOf(v) === -1) acc.push(v);
            });
            return acc;
        },
        ['key', 'session_state']
    );
};

const ForceDropdown: React.FC<{
    entry: Entry;
    entryState: unknown;
    onForce: onForceHandler;
}> = ({ entry, entryState, onForce }) => {
    const isFlag = typeof entryState === 'boolean';
    const printState = JSON.stringify(entryState);
    const variants = isFlag
        ? ['true', 'false', 'cancel']
        : [
              ...(entry?.variations?.map(({ key }) => key as string) ?? []),
              'cancel'
          ];
    const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        const isCancel = val === 'cancel';
        const newVal = isCancel ? (isFlag ? 'true' : entryState) : val;
        const urlParam = `${isCancel ? 'cancel_' : ''}${
            isFlag ? 'force_feature_flag_' : 'force_test_variation_'
        }${entry.key}`;
        onForce([urlParam, newVal as string]);
    };
    return (
        <select
            name={`override_${entry.key}`}
            onChange={onChange}
            defaultValue={entryState as string}
        >
            <option value={''} disabled>
                current state: {printState}
            </option>
            {variants.map((v) => (
                <option key={v} value={v}>
                    {v}
                </option>
            ))}
        </select>
    );
};

const TableView: React.FC<{
    data?: Entry[];
    stateResolver: StateResolver;
    forcedKeys?: string[];
    title: string;
    onForce: onForceHandler;
}> = ({ data, stateResolver, forcedKeys, title, onForce }) => {
    if (!data) return null;
    const headings = getHeadings(data);
    return (
        <StyledTable>
            <thead>
                <tr>
                    <td colSpan={headings.length}>{title}</td>
                </tr>
                <tr>
                    {headings.map((key) => (
                        <th key={key}>{key}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((entry) => {
                    const isForced = forcedKeys?.includes(entry.key);
                    return (
                        <tr
                            key={entry.id as string}
                            className={isForced ? 'forced' : ''}
                        >
                            {headings.map((k: string) => (
                                <td key={entry.id + k}>
                                    {k === 'session_state' ? (
                                        <ForceDropdown
                                            entry={entry}
                                            entryState={stateResolver(entry)}
                                            onForce={onForce}
                                        />
                                    ) : (
                                        JSON.stringify(entry[k])
                                    )}
                                </td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </StyledTable>
    );
};

export interface OptimizelyDevTogglesProps {
    optimizelyDatafile: OptimizelyDataFile;
    optimizelyOverrides?: OptimizelyOverrides;
}

const OptimizelyDevToggles = ({
    optimizely,
    optimizelyDatafile,
    optimizelyOverrides = { features: {}, experiments: {} }
}: OptimizelyDevTogglesProps & WithOptimizelyProps) => {
    const triggerOrigin = useRef(null);
    const [isOverlayVisible, openOverlay, closeOverlay] = useModal();

    const onForce: onForceHandler = ([param, val]) => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set(param, val);
        window.location.search = urlParams.toString();
    };

    const parsedOptimizelyDataFile =
        typeof optimizelyDatafile === 'string'
            ? JSON.parse(optimizelyDatafile)
            : optimizelyDatafile;

    const forcedFlags = Object.keys(optimizelyOverrides.features);
    const forcedExperiments = Object.keys(optimizelyOverrides.experiments);
    const forcedKeys = [...forcedFlags, ...forcedExperiments];

    return (
        <>
            <FixedButtonWithIcon
                iconOnly
                aria-label="optimizely"
                variant="secondary"
                onClick={() => openOverlay(triggerOrigin)}
                ref={triggerOrigin}
            >
                <OptimizelyLogo />
            </FixedButtonWithIcon>
            <Overlay isShowing={isOverlayVisible}>
                <Container>
                    <ButtonWithIcon
                        aria-label="close"
                        variant="tertiary"
                        iconOnly
                        onClick={closeOverlay}
                        style={{ float: 'right' }}
                    >
                        <CloseSmall />
                    </ButtonWithIcon>
                    <TableView
                        title="experiments"
                        data={parsedOptimizelyDataFile?.experiments}
                        stateResolver={(experiment: Entry) =>
                            optimizely?.getVariation(experiment.key)
                        }
                        forcedKeys={forcedKeys}
                        onForce={onForce}
                    />
                    <TableView
                        title="flags"
                        data={parsedOptimizelyDataFile?.featureFlags}
                        stateResolver={(flag: Entry) =>
                            getOptimizelyFeatureFlag(
                                flag.key,
                                optimizelyOverrides?.features,
                                optimizely
                            )
                        }
                        forcedKeys={forcedKeys}
                        onForce={onForce}
                    />
                </Container>
            </Overlay>
        </>
    );
};

export default withOptimizely(OptimizelyDevToggles);
